import type React from 'react';
import { Avatar, Button, StatusDot } from '@/components';
import type { AgentWithCounts } from '@/lib/api';
import './AgentsGallery.scss';

export interface AgentsGalleryProps {
  agents: AgentWithCounts[];
  connected: boolean;
  onOpenChat: (agentId: string) => void;
  onConfigure: (agentId: string) => void;
  onNewAgent: () => void;
}

/** Mirrors the prototype's meta line: source count and account access state. */
function metaLabel(agent: AgentWithCounts, connected: boolean): string {
  const parts: string[] = [];
  if (agent.useKnowledge) {
    parts.push(`${agent.sourceCount} source${agent.sourceCount === 1 ? '' : 's'}`);
  }
  if (agent.useApi) parts.push(connected ? 'Account access' : 'Account offline');
  return parts.length > 0 ? parts.join(' · ') : 'No sources';
}

export const AgentsGallery: React.FC<AgentsGalleryProps> = ({
  agents,
  connected,
  onOpenChat,
  onConfigure,
  onNewAgent,
}) => (
  <div className="screen screen--wide">
    <div className="screen__eyebrow">Locate2u Intelligence</div>
    <div className="agents__head">
      <div>
        <h1 className="screen__title">Your agents</h1>
        <p className="screen__lede">
          Create AI agents with their own purpose, rules and knowledge — connected to your live
          Locate2u account.
        </p>
      </div>
      <Button onClick={onNewAgent}>New agent</Button>
    </div>

    <div className="agents__grid">
      {agents.map((agent) => (
        <article key={agent.id} className="agent-card">
          <header className="agent-card__head">
            <Avatar name={agent.name} colour={agent.colour} size={40} />
            <div className="agent-card__identity">
              <h2 className="agent-card__name">{agent.name}</h2>
              <div className="agent-card__meta">
                <StatusDot
                  tone={agent.useApi && !connected ? 'idle' : 'live'}
                  size={7}
                />
                {metaLabel(agent, connected)}
              </div>
            </div>
          </header>

          <p className="agent-card__purpose">{agent.purpose}</p>

          <footer className="agent-card__actions">
            <Button size="sm" fullWidth onClick={() => onOpenChat(agent.id)}>
              Open chat
            </Button>
            <Button size="sm" variant="secondary" fullWidth onClick={() => onConfigure(agent.id)}>
              Configure
            </Button>
          </footer>
        </article>
      ))}

      <button type="button" className="agents__new-tile" onClick={onNewAgent}>
        <span className="agents__new-tile-plus">+</span>
        <span className="agents__new-tile-label">New agent</span>
      </button>
    </div>
  </div>
);

export default AgentsGallery;
